"use client"

import React, { useEffect, useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Loader2, Plus, X } from "lucide-react"
import { FilterValueControl, OperatorSelect, useReferenceSets } from "@/components/explorer/filter-controls"
import { hasFilterState, operatorOf, withFilterOperator, withFilterValues, withoutFilter, type FilterValues } from "@/lib/filter-values"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type { AtsFilterDefinition } from "@/types/ats-explorer-api"

export {
  REFERENCE_TYPEAHEAD_THRESHOLD,
  fromDateTimeLocal,
  getMissingRequiredFilters,
  isValuelessOperator,
  toDateTimeLocal,
  toExplorerFilters,
  type FilterValueState,
  type FilterValues,
} from "@/lib/filter-values"

export interface FilterBarProps {
  atsSystem: string
  /** The entity the definitions belong to; explicitly-added filters reset when it changes */
  entityName: string
  /** The selected entity's declared filters */
  definitions: AtsFilterDefinition[]
  values: FilterValues
  onChange: (values: FilterValues) => void
  /** True while the complete (metadata-derived) filter set is still loading */
  isLoadingDefinitions?: boolean
}

/**
 * Inline, on-demand filter row: required filters and explicitly added (or valued)
 * filters render; the rest live behind an "Add filter" picker. Used where a compact
 * inline form fits (the pull wizard's scope step); the explorer uses the filter blade.
 */
export function FilterBar({ atsSystem, entityName, definitions, values, onChange, isLoadingDefinitions = false }: FilterBarProps) {
  const { t } = useTranslation()
  const [addedNames, setAddedNames] = useState<Set<string>>(new Set())
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [pickerOpen, setPickerOpen] = useState(false)

  // Forget explicitly-added filters when the entity changes. Keyed on the entity, not
  // the definitions: the set GROWS when the metadata-derived filters arrive, and that
  // must not drop what the user added.
  useEffect(() => {
    setAddedNames(new Set())
    setDrafts({})
  }, [entityName])

  const visibleDefinitions = useMemo(
    () => definitions.filter((d) => d.isRequired || addedNames.has(d.name) || hasFilterState(values[d.name])),
    [definitions, addedNames, values]
  )
  const availableDefinitions = useMemo(() => definitions.filter((d) => !visibleDefinitions.includes(d)), [definitions, visibleDefinitions])
  const referenceSets = useReferenceSets(atsSystem, visibleDefinitions)

  if (definitions.length === 0 && !isLoadingDefinitions) {
    return null
  }

  const addFilter = (name: string) => {
    setAddedNames((current) => new Set(current).add(name))
    setPickerOpen(false)
  }

  const removeFilter = (name: string) => {
    setAddedNames((current) => {
      const next = new Set(current)
      next.delete(name)
      return next
    })
    setDrafts((current) => {
      const next = { ...current }
      delete next[name]
      return next
    })
    onChange(withoutFilter(values, name))
  }

  const standard = availableDefinitions.filter((d) => !d.isCustom)
  const custom = availableDefinitions.filter((d) => d.isCustom)

  return (
    <div className="flex flex-wrap items-start gap-3">
      {visibleDefinitions.map((definition) => (
        <div key={definition.name} className="min-w-44 max-w-full space-y-1">
          {/* One line, so every block's controls and "Add filter" sit at the same height */}
          <div className="flex items-center justify-between gap-2">
            <Label className="flex min-w-0 text-xs text-muted-foreground" title={`${definition.displayName} (${definition.name})`}>
              <span className="truncate">{definition.displayName}</span>
              {definition.isRequired && <span className="ml-0.5 text-destructive">*</span>}
            </Label>
            {!definition.isRequired && (
              <button type="button" className="text-muted-foreground hover:text-foreground" title={t("explorer.filterBar.removeFilter", { name: definition.displayName })} onClick={() => removeFilter(definition.name)}>
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="flex gap-1">
            <OperatorSelect definition={definition} value={operatorOf(values, definition)} onChange={(op) => onChange(withFilterOperator(values, definition, op))} className="h-8 w-24 shrink-0 text-xs" />
            <div className="min-w-28 flex-1">
              <FilterValueControl
                atsSystem={atsSystem}
                definition={definition}
                state={values[definition.name]}
                operator={operatorOf(values, definition)}
                referenceItems={referenceSets[definition.referenceType ?? ""] ?? []}
                onChange={(next, labels) => onChange(withFilterValues(values, definition, next, labels))}
                draft={drafts[definition.name]}
                onDraftChange={(draft) => setDrafts((current) => ({ ...current, [definition.name]: draft }))}
              />
            </div>
          </div>
        </div>
      ))}

      {(availableDefinitions.length > 0 || isLoadingDefinitions) && (
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          {/* Shaped like a filter block, with an empty label row, so the button sits on the controls line */}
          <div className="space-y-1">
            {visibleDefinitions.length > 0 && (
              <Label aria-hidden className="invisible flex text-xs">
                &nbsp;
              </Label>
            )}
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 border-dashed text-xs">
                <Plus className="mr-1 h-3.5 w-3.5" /> {t("explorer.filterBar.addFilter")}
                {isLoadingDefinitions && <Loader2 className="ml-1 h-3 w-3 animate-spin" />}
              </Button>
            </PopoverTrigger>
          </div>
          <PopoverContent className="w-72 p-0" align="start">
            <Command>
              <CommandInput placeholder={t("explorer.filterBar.searchFilters")} className="h-8 text-xs" />
              <CommandList className="max-h-80">
                <CommandEmpty>{t((isLoadingDefinitions ? "explorer.filterBar.loadingFilters" : "explorer.filterBar.noMatchingFilter"))}</CommandEmpty>
                <FilterPickerGroup heading={custom.length > 0 ? t("explorer.filters.title") : undefined} definitions={standard} onSelect={addFilter} />
                <FilterPickerGroup heading={t("explorer.filters.groups.custom")} definitions={custom} onSelect={addFilter} />
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}

function FilterPickerGroup({ heading, definitions, onSelect }: { heading?: string; definitions: AtsFilterDefinition[]; onSelect: (name: string) => void }) {
  if (definitions.length === 0) return null
  return (
    <CommandGroup heading={heading}>
      {definitions.map((definition) => (
        <CommandItem key={definition.name} value={`${definition.displayName} ${definition.name}`} onSelect={() => onSelect(definition.name)} className="text-xs">
          <span className="truncate">{definition.displayName}</span>
          {definition.displayName !== definition.name && (
            <span className="ml-auto max-w-28 truncate pl-2 font-mono text-[10px] text-muted-foreground">{definition.name}</span>
          )}
        </CommandItem>
      ))}
    </CommandGroup>
  )
}
